import React, { useState, useMemo, useCallback } from 'react';
import _get from 'lodash/get';
import { StacklineColor, useStacklineTheme, SlDropdownMenuOption } from '@stackline/ui';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import LegendWithMetric, { LegendWithMetricProps } from '../Legend/LegendWithMetric';
import { AggregationField } from 'src/types/application/types';
import CommonSummaryWithDropdown from 'src/components/BeaconV2Components/common/CommonSummaryWithDropdown';
import { useStyles } from 'src/components/BeaconV2Components/EntityTableRefresh/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SlDropdownMenu, SlDropdownMenuProps } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import Box from '@mui/material/Box';
import {
  useChartContextDisableSubtitleFallback,
  useChartContextGroup,
  useChartContextHideSubtitle,
  useChartContextTitle
} from 'src/utils/chartContext/ChartContextGroupContext';
import domtoimage from 'src/utils/dom-to-imagev2';
import saveAs from 'file-saver';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { useSnackbar } from 'src/utils/Hooks/typedHooks';
import { defaultImageOption } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useCsvExporter';
import { useAskAdvisorAction } from 'src/components/AtlasV2Components/Common/useAskAdvisorAction';
import { AskAdvisorAIAnimation } from 'src/components/Animations/AskAdvisorAIAnimation/AskAdvisorAIAnimation';
import { captureElementAsWebp } from 'src/utils/chartContext/captureScreenshot';
import { getChartContextGroup } from 'src/utils/chartContext/chartContextRegistry';

/**
 * Extended dropdown menu option that supports an optional endAdornment
 * (e.g. a Lottie animation icon) rendered after the label text.
 */
export interface ExtendedDropdownMenuOption extends SlDropdownMenuOption {
  endAdornment?: React.ReactNode;
}

/**
 * Metadata about the chart section, extracted from the DOM at click time.
 * Passed to `handleAskAdvisor` so the backend receives the visible
 * title and subtitle as `appMetadata.title` / `appMetadata.subtitle`.
 */
export interface ChartSectionMeta {
  /** Sanitized visible title text (e.g. "Total Traffic by Keyword") */
  title?: string;
  /** Sanitized visible subtitle/description text */
  subtitle?: string;
}

/** Collapse whitespace left by dropdown-icon elements and trim. */
const sanitizeText = (el?: HTMLElement | null): string | undefined => {
  const raw = el?.innerText;
  if (!raw) return undefined;
  return raw.replace(/\s+/g, ' ').trim() || undefined;
};

export interface BeaconChartWithLegendProps {
  children: React.ReactNode;
  title?: React.ReactNode;
  comparisonLegendProps?: Omit<LegendWithMetricProps, 'color'> & { color?: StacklineColor } & { opacity?: number };
  primaryLegendProps?: Omit<LegendWithMetricProps, 'color'> & { color?: StacklineColor };
  groupByFields?: React.ComponentProps<typeof CommonSummaryWithDropdown>['groupByFields'];
  selectedGroupBy?: AggregationField;
  handleGroupBy?: (id: string) => void;
  handleMainMetric?: (id: string) => void;
  mainMetricFields?: React.ComponentProps<typeof CommonSummaryWithDropdown>['mainMetricFields'];
  selectedMainMetric?: AggregationField;
  selectedGroupByField?: string;
  loading?: boolean;
  loadingComponent?: React.ReactNode;
  subtitle?: string | React.ReactNode;
  disableExportCsv?: boolean;
  convertSeriesToDelimitedData?: () => Promise<any>;
  /**
   * Diplays the primary legend indicator as dashed when true
   */
  primaryDashed?: boolean;
  /**
   * Color for the secondary legend indicator
   */
  secondaryColor?: StacklineColor;
  showTitleSummary?: boolean;
  singleDropdownProps?: SlDropdownMenuProps & { prefix?: string } & { suffix?: string };
  /**
   * Optional builder for the chart dropdown options. Should be combined with
   * `dropdownOptionHandler` to handle the click event. Passes in the default
   * dropdown options and returns the new options.
   */
  buildDropdownMenuOptions?: (options: ExtendedDropdownMenuOption[]) => ExtendedDropdownMenuOption[];
  /**
   * Optional handler for the dropdown options. Can be combined with
   * `buildDropdownMenuOptions` to build the options if adding
   * custom options.
   */
  dropdownOptionHandler?: (option: ExtendedDropdownMenuOption, defaultDropdownHandler: () => Promise<void>) => void;
  showExportMenu?: boolean;
  saveImageRef?: React.RefObject<HTMLDivElement>;

  customLegends?: React.ReactElement;
  includeSummaryInfo?: boolean;
  subtitleSX?: React.CSSProperties;
  containerStyles?: React.CSSProperties;
  /**
   * Optional context group identifier for chart context scoping.
   * Falls back to the value from ChartContextGroupProvider (React Context)
   * if not passed as a prop. When resolved (from prop or context):
   * - The "Ask Advisor" menu item is automatically appended to the
   *   ellipsis dropdown (no external wiring needed).
   *
   * The ChartContextGroupProvider should be placed at the **layout level**
   * to give explicit control over which sections expose Ask Advisor.
   */
  contextGroup?: string;
}

/**
 * Generic container for charts with a legend. The chart to render should be passed in as a child.
 */
const BeaconChartWithLegend = ({
  children,
  comparisonLegendProps,
  primaryLegendProps,
  title = '',
  groupByFields,
  handleGroupBy,
  handleMainMetric,
  selectedGroupBy,
  mainMetricFields,
  selectedMainMetric,
  loading,
  subtitle,
  loadingComponent = <SplineChartLoading />,
  subtitleSX,
  primaryDashed,
  secondaryColor,
  disableExportCsv = false,
  showTitleSummary = true,
  singleDropdownProps,
  convertSeriesToDelimitedData,
  buildDropdownMenuOptions,
  dropdownOptionHandler,
  showExportMenu = true,
  customLegends,
  includeSummaryInfo = true,
  /**
   * Ref to the container to save as an image
   */
  saveImageRef,
  containerStyles = {},
  contextGroup: contextGroupProp
}: BeaconChartWithLegendProps) => {
  const classes = useStyles();
  const theme = useStacklineTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { showSnackbar } = useSnackbar();

  // Resolve contextGroup: explicit prop takes precedence, then React Context.
  // The provider is at the layout level for explicit control over which
  // sections expose Ask Advisor.
  const contextGroupFromCtx = useChartContextGroup();
  const hideSubtitleFromCtx = useChartContextHideSubtitle();
  const disableSubtitleFallbackFromCtx = useChartContextDisableSubtitleFallback();
  const contextGroup = contextGroupProp || contextGroupFromCtx;
  const subtitleContent = hideSubtitleFromCtx ? undefined : subtitle;
  const shouldShowSummaryInfoSubtitle =
    includeSummaryInfo || (!!hideSubtitleFromCtx && !disableSubtitleFallbackFromCtx);

  // Resolve description automatically from the registry based on contextGroup.
  const contextDescription = contextGroup ? getChartContextGroup(contextGroup)?.description : undefined;

  // chartTitle from ChartContextProvider — used as the visible title sent to Advisor
  // when the DOM title can't be extracted (e.g. nested charts with showTitleSummary=false).
  const chartTitleFromCtx = useChartContextTitle();

  // Auto-wire Ask Advisor when contextGroup is resolved (from prop or context).
  // shouldShowAdvisor will be false when contextGroup is undefined
  // (because useAskAdvisorAction checks !!contextGroup internally).
  const { handleAskAdvisor, shouldShow: shouldShowAdvisor } = useAskAdvisorAction({
    contextGroup,
    description: contextDescription
  });

  const handleOpenExports = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const titleRef = React.useRef<HTMLSpanElement>(null);
  const subtitleRef = React.useRef<HTMLDivElement>(null);

  const handleDropdownClick = useCallback(
    async (option: ExtendedDropdownMenuOption) => {
      // Auto-handle Ask Advisor click (injected by BeaconChartWithLegend itself).
      // Passes the screenshot capture as a Promise (not awaited) so that
      // handleAskAdvisor can start the presigned URL fetch in parallel with
      // the DOM rendering + PNG→WebP conversion.
      if (option.id === 'askAdvisor') {
        setAnchorEl(null); // Close menu immediately for better UX

        // Extract visible title/subtitle from the DOM for Advisor context.
        //
        // Title: use the prop directly when it's a plain string to avoid capturing
        // summary info text (e.g. "All Categories") that CommonSummaryInfoSubtitle
        // renders alongside the title. Fall back to DOM extraction for dynamic
        // dropdown-driven titles where the prop is JSX.
        //
        // Subtitle: prefer the dedicated subtitle area (subtitleRef). When no subtitle
        // prop is provided (e.g. the Traffic donut section), extract from the title
        // area by removing the known title string, leaving just the summary info
        // (e.g. "All Categories").
        const titleAreaText = sanitizeText(titleRef.current);
        const titleText = typeof title === 'string' && title ? title : (titleAreaText || chartTitleFromCtx);
        const subtitleText =
          sanitizeText(subtitleRef.current) ||
          (typeof title === 'string' && titleAreaText ? titleAreaText.replace(title, '').trim() : undefined);

        const element = saveImageRef?.current ?? chartContainerRef.current;
        const screenshotPromise = element ? captureElementAsWebp(element) : undefined;
        const chartHeight = element?.offsetHeight;

        handleAskAdvisor(screenshotPromise, titleText, subtitleText || undefined, chartHeight);
        return;
      }

      // Save Image / Copy Image / Export CSV — unchanged, still uses PNG via domtoimage.toBlob
      const defaultDropdownHandler = async () => {
        try {
          const file_name =
            (typeof title === 'string' ? title : null) || _get(selectedMainMetric, 'displayName') || 'chart';
          switch (option.id) {
            case 'saveImage':
            case 'copyImage': {
              window.focus();
              // Wait for focus and potential rendering delays
              await new Promise((resolve) => setTimeout(resolve, 200));

              const maxWaitTime = 2000;
              const pollInterval = 100;
              const startTime = Date.now();

              while (!chartContainerRef.current && Date.now() - startTime < maxWaitTime) {
                await new Promise((resolve) => setTimeout(resolve, pollInterval));
              }

              // Wait for the ref to become available, with max waiting time of 2 seconds
              if (!chartContainerRef.current) {
                console.error(`Chart container ref not available after ${maxWaitTime}ms`);
                showSnackbar({ type: 'error', message: 'Error copying image: Chart not ready.' });
                return;
              }
              const blob = (await domtoimage.toBlob(
                saveImageRef?.current ?? (chartContainerRef.current as HTMLDivElement),
                defaultImageOption
              )) as Blob;

              if (!blob) {
                console.error(`domtoimage.toBlob returned null for chart: ${title} chart`);
                showSnackbar({ type: 'error', message: 'Failed to generate image for copying.' });
                return;
              }

              if (option.id === 'saveImage') {
                saveAs(blob, `${file_name}.png`);
              } else if (option.id === 'copyImage') {
                if (blob) {
                  const clipboardItem = new ClipboardItem({ 'image/png': blob });
                  await navigator.clipboard.write([clipboardItem]);
                  showSnackbar({ type: 'success', message: 'Image copied to clipboard!' });
                } else {
                  console.error(`Unable to generate blob for chart: ${title}`);
                }
              }
              setAnchorEl(null);
              break;
            }

            case 'exportCsv': {
              if (convertSeriesToDelimitedData) {
                // Export as csv
                await convertSeriesToDelimitedData();
              }
              setAnchorEl(null);
              break;
            }

            default:
          }
        } catch (e) {
          console.error('Error exporting chart:', e);
        }
      };

      if (dropdownOptionHandler) {
        dropdownOptionHandler(option, defaultDropdownHandler);
      } else {
        defaultDropdownHandler();
      }
    },
    [
      handleAskAdvisor,
      dropdownOptionHandler,
      title,
      chartTitleFromCtx,
      selectedMainMetric,
      convertSeriesToDelimitedData,
      saveImageRef,
      showSnackbar
    ]
  );

  const handleCloseExports = () => {
    setAnchorEl(null);
  };

  const exportButton = showExportMenu ? (
    <IconButton
      onClick={handleOpenExports}
      data-screenshot-exclude
      sx={{
        height: theme.spacing.mdl,
        width: theme.spacing.mdl,
        borderRadius: theme.spacing.xs,
        color: theme.colors.primary
      }}
    >
      <MoreHorizIcon />
    </IconButton>
  ) : null;

  const dropdownOptions: ExtendedDropdownMenuOption[] = useMemo(() => {
    const defaultOptions: ExtendedDropdownMenuOption[] = [
      {
        id: 'saveImage',
        label: 'Save image'
      },
      {
        id: 'copyImage',
        label: 'Copy image'
      },
      ...(disableExportCsv
        ? []
        : [
            {
              id: 'exportCsv',
              label: 'Export CSV'
            }
          ])
    ];

    // Let callers add their own custom options first (e.g. "Reset brands")
    const composedOptions = buildDropdownMenuOptions ? buildDropdownMenuOptions(defaultOptions) : defaultOptions;

    // Auto-append Ask Advisor when contextGroup is set and feature flag is on
    if (shouldShowAdvisor) {
      composedOptions.push({
        id: 'askAdvisor',
        label: 'Ask Advisor',
        endAdornment: <AskAdvisorAIAnimation size={24} />
      });
    }

    return composedOptions;
  }, [disableExportCsv, buildDropdownMenuOptions, shouldShowAdvisor]);

  return (
    <div style={{ backgroundColor: 'white', ...containerStyles }} ref={chartContainerRef}>
      <Flex gap={subtitleContent && !loading ? 0 : 'md'} flexDirection="column">
        {showTitleSummary && (
          <Box
            marginBottom={subtitleContent ? theme.spacing.none : theme.spacing.md}
            // if we remove summaryInfo (All Categories, Keyword List), Title and subtitle has to be "8px")
            sx={{ transform: shouldShowSummaryInfoSubtitle || subtitleContent ? 'none' : 'translateY(8px)' }}
          >
            <Flex justifyContent="space-between">
              {groupByFields && groupByFields.length > 1 ? (
                <Flex flexDirection="column">
                  <span ref={titleRef}>
                    <CommonSummaryWithDropdown
                      prefixTitle={`${title} `}
                      handleGroupByChange={(id) => handleGroupBy(id)}
                      groupByFields={groupByFields}
                      selectedGroupBy={selectedGroupBy}
                      mainMetricFields={mainMetricFields}
                      handleMainMetric={handleMainMetric}
                      selectedMainMetric={selectedMainMetric}
                      shouldShowFullItemText
                    />
                  </span>
                  {hideSubtitleFromCtx && !disableSubtitleFallbackFromCtx ? (
                    <div>
                      <CommonSummaryInfoSubtitle title="" includeSubtitle={shouldShowSummaryInfoSubtitle} />
                    </div>
                  ) : (
                    <CommonSummaryInfoSubtitle title="" includeSubtitle={shouldShowSummaryInfoSubtitle} />
                  )}
                </Flex>
              ) : (
                <span ref={titleRef}>
                  {hideSubtitleFromCtx && !disableSubtitleFallbackFromCtx ? (
                    <div>
                      <CommonSummaryInfoSubtitle
                        title={
                          singleDropdownProps ? (
                            <div className={classes.entityTableHeaderContainer}>
                              <Flex gap="sm" alignItems="center">
                                {singleDropdownProps?.prefix && <Text variant="h4">{singleDropdownProps?.prefix}</Text>}
                                <SlDropdownMenu {...singleDropdownProps} buttonVariant="title" shouldShowFullItemText />
                                {singleDropdownProps?.suffix && <Text variant="h4">{singleDropdownProps?.suffix}</Text>}
                              </Flex>
                            </div>
                          ) : (
                            title
                          )
                        }
                        includeSubtitle={shouldShowSummaryInfoSubtitle}
                      />
                    </div>
                  ) : (
                    <CommonSummaryInfoSubtitle
                      title={
                        singleDropdownProps ? (
                          <div className={classes.entityTableHeaderContainer}>
                            <Flex gap="sm" alignItems="center">
                              {singleDropdownProps?.prefix && <Text variant="h4">{singleDropdownProps?.prefix}</Text>}
                              <SlDropdownMenu {...singleDropdownProps} buttonVariant="title" shouldShowFullItemText />
                              {singleDropdownProps?.suffix && <Text variant="h4">{singleDropdownProps?.suffix}</Text>}
                            </Flex>
                          </div>
                        ) : (
                          title
                        )
                      }
                      includeSubtitle={shouldShowSummaryInfoSubtitle}
                    />
                  )}
                </span>
              )}
              {!loading && exportButton}
            </Flex>
          </Box>
        )}
        {!loading && subtitleContent && typeof subtitleContent === 'string' && (
          <div ref={subtitleRef}>
            <Text variant="body1" sx={{ marginBottom: theme.spacing.md, ...subtitleSX }}>
              {subtitleContent}
            </Text>
          </div>
        )}
        {!loading && subtitleContent && typeof subtitleContent !== 'string' && (
          <div ref={subtitleRef}>{subtitleContent}</div>
        )}
        <Flex flexDirection="column" gap="lg">
          {loading ? (
            loadingComponent
          ) : (
            <Flex gap="lg" flexDirection="column">
              {(primaryLegendProps || !showTitleSummary) && (
                <Flex justifyContent="space-between">
                  <Flex gap="lg">
                    {primaryLegendProps && (
                      <>
                        {comparisonLegendProps && (
                          <LegendWithMetric {...comparisonLegendProps} color={secondaryColor || 'casper'} />
                        )}
                        <LegendWithMetric
                          {...primaryLegendProps}
                          color={primaryLegendProps.color || 'primary'}
                          dashed={primaryDashed}
                        />
                      </>
                    )}
                  </Flex>
                  {!showTitleSummary && exportButton}
                </Flex>
              )}
              {customLegends}
              {children}
            </Flex>
          )}
        </Flex>
      </Flex>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleCloseExports}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '4px',
              boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.1)'
            }
          }
        }}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        {dropdownOptions.map((option) => (
          <MenuItem
            key={option.id}
            disabled={option.disabled}
            onClick={() => handleDropdownClick(option)}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: 'Roboto, sans-serif',
              color: '#031425',
              letterSpacing: '0.08px',
              gap: '4px',
              padding: '8px 12px'
            }}
          >
            {option.label}
            {option.endAdornment}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default BeaconChartWithLegend;
